/* eslint-disable react/require-default-props */
import {
    AnchorHTMLAttributes,
    ForwardRefExoticComponent,
    MouseEvent,
    ReactElement,
    ReactNode,
    RefAttributes,
    forwardRef,
} from 'react';
import useScrollLock from '@activebrands/core-web/libs/scroll';
import { StyletronStyle } from '@activebrands/core-web/types/styletron';
import { scrollTo } from '@grebban/utils/function/scroll';
import { Link as GatsbyLink } from 'gatsby';

/**
 * @params {string} [to] - Internal or extranal href.
 * @param {boolean} [isVolatile=false] - If true and to is undefined, render a 'a' tag without href attr.
 */

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    $style?: StyletronStyle;
    children?: ReactNode;
    isVolatile?: boolean;
    searchMeta?: unknown;
    to?: string;
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
    ({ isVolatile = false, to, onClick = () => null, ...rest }, ref) => {
        if (!to && !isVolatile) {
            return (rest.children ?? null) as ReactElement | null;
        }

        const { unlockScroll } = useScrollLock();
        // If the link begins with /, ? or #
        const isInternal = /^[/?#](?!\/)/.test(to || '');

        const state = rest?.searchMeta && { searchMeta: rest.searchMeta };

        // Use Gatsby Link for internal links, and <a> for others
        if (isInternal && to) {
            return (
                <GatsbyLink
                    to={to}
                    state={state}
                    {...rest}
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                        onClick(e);

                        // Handle anchor tags on the same page
                        // Note: Anchor tag links on new pages gets handled inside gatsby-browser
                        const hash = to.match(/#([\w-])+/)?.[0];
                        const element = hash ? document.querySelector(hash) : null;

                        // Unlock scroll in case overlay is open when changeing route
                        unlockScroll();

                        if (element) {
                            e.preventDefault();
                            const topOffset = element.getBoundingClientRect().top;
                            scrollTo({
                                top: document.documentElement.scrollTop + topOffset,
                                eventDetails: { hash },
                            });
                        }
                    }}
                />
            );
        }

        return (
            <a
                href={to}
                ref={ref}
                rel={rest.rel || 'noopener'}
                target={rest.target || '_blank'}
                onClick={onClick}
                {...rest}
            />
        );
    }
) as ForwardRefExoticComponent<LinkProps & RefAttributes<HTMLAnchorElement>>;

Link.displayName = 'Link';

export default Link;
